import { useCallback, useEffect, useRef } from 'react';

import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useAppliedDiscountOffer } from 'features/offer/hooks/use-applied-discount-offer';
import { useEnableLoyaltyServiceOffers } from 'features/offer/hooks/use-enable-loyalty-service-offers';

import { toaster } from 'components/toaster/toaster';
import { useCart } from 'hooks/use-cart';
import { useCartHydration } from 'hooks/use-cart-hydration.ts';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { useCartContext } from 'state/cart';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LoyaltyAppliedOffer, LoyaltyOffer } from 'state/loyalty/types';
import { isDiscountOffer } from 'state/loyalty/utils';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';

import { useLoyaltyOfferAvailabilityEvaluation } from './use-loyalty-offer-availability-evaluation';

/**
 * This hook is used to remove an expired offer from the cart and displays a toast notification to the user.
 * Once the offersFeedbackMap is available, it will check if there is an offer with an error without threshold.
 * If there is, it will remove the offer from the cart and display a toast notification to the user.
 */
export const useRemoveExpiredOfferFromCart = () => {
  const dispatch = useAppDispatch();
  const { cartEntries, removeFromCart } = useCart();
  const { cartEntries: minimalCartEntries } = useMinimalCart();
  const { isStoreChangeHydrationInProgress } = useCartHydration({
    cartEntries,
    minimalCartEntries,
  });

  const { indexedMenu } = useUnifiedMenuContext();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const toastWasDisplayed = useRef(false);

  const { enabled: loyaltyServiceOffersEnabled } = useEnableLoyaltyServiceOffers();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  // Flag-aware applied-discount lookup: when the loyalty service is disabled
  // this reads `cmsOffers`; when enabled it reads the offer provider's retained
  // batch metadata, so the discount offer is still found without depending on
  // `cmsOffers`.
  const appliedLoyaltyDiscountOffer = useAppliedDiscountOffer();
  const selectedOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  // Set at price-start and while a cart-entries offer evaluation is in
  // flight (see `YourCart` and `useLoyaltyCartEntriesEvaluation`). Gating on
  // it here prevents evicting an offer against a feedback map that was just
  // reset on mount (e.g. navigating checkout -> cart) and hasn't been
  // re-evaluated against the freshly repriced order yet.
  const offersEvaluating = useAppSelector(selectors.loyalty.selectOffersEvaluating);
  // `offersEvaluating` is set to `true` by a SIBLING effect in `YourCart`
  // (`index.tsx`), derived from this same `isOrderPriceLoading` value — but
  // that dispatch only lands on a LATER render/commit. On first mount with a
  // reprice already in flight (e.g. navigating checkout -> cart), this
  // hook's own eviction effect can run in the same commit BEFORE that
  // dispatch, still reading stale `offersEvaluating: false`. Reading
  // `isOrderPriceLoading` directly (Apollo's own synchronous `loading` value,
  // not a state dispatch round-trip) closes that one-render gap.
  const { isOrderPriceLoading } = useCartContext();
  const { isOfferUnavailable } = useLoyaltyOfferAvailabilityEvaluation();
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();

  const removeOffer = useCallback(
    (offer: LoyaltyAppliedOffer | LoyaltyOffer) => {
      dispatch(actions.loyalty.removeAppliedOffer(offer));
      if ('loyaltyEngineId' in offer && isDiscountOffer(offer)) {
        dispatch(actions.loyalty.resetAppliedDiscountOffers());
        // Clear selectedOffer if it matches the discount offer being removed
        if (selectedOffer?.loyaltyEngineId === offer.loyaltyEngineId) {
          dispatch(actions.loyalty.setSelectedOffer(null));
        }
      } else if ('cartId' in offer && offer.cartId) {
        removeFromCart({ cartId: offer.cartId });
      }
      toaster.informative(t('removeExpiredOfferItem'));
      toastWasDisplayed.current = true;
    },
    [dispatch, removeFromCart, selectedOffer]
  );

  const handleExpiredOfferRemoval = useCallback(() => {
    if (toastWasDisplayed.current) {
      return;
    }

    const appliedOffer = appliedOffers.find(offer =>
      cartEntries.some(entry => entry.cartId === offer.cartId)
    );

    // Only one selected offer can exist in the cart.
    const offer = appliedOffer || appliedLoyaltyDiscountOffer;

    if (offer && isOfferUnavailable(offer)) {
      removeOffer(offer);
    }

    // Also check if selectedOffer is unavailable even if not in appliedOffers
    // This handles cases where discount offers may not be tracked in appliedOffers
    // Use checkLoyaltyOfferAvailability which properly checks store restrictions
    const isSelectedOfferAvailable = selectedOffer
      ? checkLoyaltyOfferAvailability(selectedOffer)
      : true;

    if (selectedOffer && !offer && !isSelectedOfferAvailable) {
      dispatch(actions.loyalty.setSelectedOffer(null));
      // Also clear the feedback map to remove any lingering errors from the removed offer
      dispatch(actions.loyalty.resetOfferFeedbackMap());
      toaster.informative(t('removeExpiredOfferItem'));
      toastWasDisplayed.current = true;
    }
  }, [
    appliedOffers,
    appliedLoyaltyDiscountOffer,
    isOfferUnavailable,
    cartEntries,
    removeOffer,
    selectedOffer,
    dispatch,
    checkLoyaltyOfferAvailability,
  ]);

  useEffect(() => {
    // When the loyalty service is enabled, front-end availability recomputation
    // is bypassed — eviction instead keys on absence from the settled
    // applied-offers batch response (`useLoyaltyServiceAppliedOfferEviction`).
    // This front-end recompute removal runs only when the loyalty service is
    // disabled.
    if (loyaltyServiceOffersEnabled) {
      return;
    }
    const isPricingInProgress = isOrderPriceLoading && appliedOffers.length > 0;
    if (
      offersFeedbackMap &&
      !isStoreChangeHydrationInProgress &&
      !offersEvaluating &&
      !isPricingInProgress &&
      (!isUnifiedMenuEnabled || indexedMenu.isLoaded())
    ) {
      handleExpiredOfferRemoval();
    }
  }, [
    loyaltyServiceOffersEnabled,
    offersFeedbackMap,
    handleExpiredOfferRemoval,
    isStoreChangeHydrationInProgress,
    offersEvaluating,
    isOrderPriceLoading,
    appliedOffers.length,
    isUnifiedMenuEnabled,
    indexedMenu,
  ]);

  // Fallback: if all offers were cleared (no applied offers, no selected offer)
  // but feedback map still has errors (e.g., store change), clear the feedback map
  useEffect(() => {
    const hasFeedback = offersFeedbackMap && Object.keys(offersFeedbackMap).length > 0;
    const noOffersSelected = appliedOffers.length === 0 && !selectedOffer;

    if (hasFeedback && noOffersSelected) {
      dispatch(actions.loyalty.resetOfferFeedbackMap());
    }
  }, [offersFeedbackMap, appliedOffers.length, selectedOffer, dispatch]);
};
